export type PoseJointId =
  | 'head'
  | 'neck'
  | 'left_shoulder'
  | 'left_elbow'
  | 'left_wrist'
  | 'right_shoulder'
  | 'right_elbow'
  | 'right_wrist'
  | 'hip'
  | 'left_hip'
  | 'left_knee'
  | 'left_ankle'
  | 'right_hip'
  | 'right_knee'
  | 'right_ankle';

export type PosePoint = { x: number; y: number };

export type PoseJoints = Record<PoseJointId, PosePoint>;

export type PoseParams = {
  /** normalized coordinates in [0..1] within the pose canvas */
  joints: PoseJoints;
  /** keep bone segment lengths stable while dragging joints */
  lockLimbLength?: boolean;
};

export const DEFAULT_POSE_JOINTS: PoseJoints = {
  // More human-like neutral stance.
  // Centered vertically within the canvas (shifted up slightly so top/bottom padding is more balanced).
  head: { x: 0.5, y: 0.11 },
  neck: { x: 0.5, y: 0.23 },
  left_shoulder: { x: 0.40, y: 0.26 },
  // Symmetric (mirror around x=0.5) and rounded to 2 decimals.
  left_elbow: { x: 0.35, y: 0.41 },
  left_wrist: { x: 0.33, y: 0.53 },
  right_shoulder: { x: 0.60, y: 0.26 },
  right_elbow: { x: 0.65, y: 0.41 },
  right_wrist: { x: 0.67, y: 0.53 },
  // Pelvis: keep center hip and separate left/right hip enough to avoid overlap in small preview.
  hip: { x: 0.5, y: 0.50 },
  left_hip: { x: 0.44, y: 0.54 },
  left_knee: { x: 0.44, y: 0.76 },
  left_ankle: { x: 0.44, y: 0.90 },
  right_hip: { x: 0.56, y: 0.54 },
  right_knee: { x: 0.56, y: 0.76 },
  right_ankle: { x: 0.56, y: 0.90 },
};

export const DEFAULT_POSE_PARAMS: PoseParams = {
  joints: DEFAULT_POSE_JOINTS,
  lockLimbLength: true,
};

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isBool = (v: unknown): v is boolean => typeof v === 'boolean';

export const normalizePoseParams = (
  raw: Record<string, unknown> | undefined
): { params: PoseParams; needsHydrate: boolean } => {
  const r = raw ?? {};
  const jointsRaw = (r as any).joints;
  const nextJoints: PoseJoints = { ...DEFAULT_POSE_JOINTS };

  let changed = false;
  if (jointsRaw && typeof jointsRaw === 'object') {
    for (const k of Object.keys(DEFAULT_POSE_JOINTS) as PoseJointId[]) {
      const p = (jointsRaw as any)[k];
      const x = isNum(p?.x) ? clamp01(p.x) : DEFAULT_POSE_JOINTS[k].x;
      const y = isNum(p?.y) ? clamp01(p.y) : DEFAULT_POSE_JOINTS[k].y;
      nextJoints[k] = { x, y };
      if (!p || !isNum(p?.x) || !isNum(p?.y) || p.x !== x || p.y !== y) {
        changed = true;
      }
    }
  } else {
    changed = true;
  }

  const lockLimbLengthRaw = (r as any).lockLimbLength;
  const lockLimbLength = isBool(lockLimbLengthRaw) ? lockLimbLengthRaw : true;
  if (!isBool(lockLimbLengthRaw) || lockLimbLengthRaw !== lockLimbLength) changed = true;

  const params: PoseParams = { joints: nextJoints, lockLimbLength };
  const needsHydrate =
    changed ||
    JSON.stringify((r as any).joints ?? null) !== JSON.stringify(params.joints) ||
    (r as any).lockLimbLength !== params.lockLimbLength;
  return { params, needsHydrate };
};


